/**
 * Notes tab — the chart's visit notes AND the note-drafting flow.
 *
 * Drafting lives HERE, not on the Scribe tab. "Create note" that navigates the
 * clinician to a different tab is the opposite of the ask: the button should
 * start the thing it names.
 *
 * `VisitNoteDrafter` is the only component in @astrana/ui that holds a write
 * seam. The record-and-transcribe path (`AmbientScribePanel`, on the Scribe
 * tab) produces no note and holds no seam, so the two can coexist on separate
 * tabs without ever producing competing drafts for one encounter — which
 * matters because the write seam APPENDS.
 *
 * Once drafting starts it never unmounts. Unmounting would destroy the session
 * and silently discard an in-progress draft, so `drafting` latches true and the
 * drafter's own "start over" is the way back — not this component.
 */
import { useMemo, useState } from "react";
import { VisitNoteDrafter } from "@astrana/ui/scribe";
import type { EhrEncounterSnapshot, WriteSectionFn } from "@astrana/ui/scribe";

import { featureFlags } from "../../config/featureFlags";
import { ChartNotesPanel } from "./ChartNotesPanel";
import { visitNoteTypeFromTemplateName } from "./elationNoteTemplateType";
import { elationSectionCandidates } from "./elationNoteSections";
import type { ElationReportRead } from "@ext/elation/elationReportsRead";
import { imagingStudiesFromReports } from "./imagingStudiesFromReports";
import {
  useOnSaveVisitNoteDraft,
  useVisitNoteDraftInitial,
} from "./visitNoteDraftPersistence";
import { VISIT_NOTE_TEMPLATE_OVERRIDES } from "./visitNoteTemplateDefaults";
import type { VisitNoteDrafterProps } from "@astrana/ui/scribe";

/**
 * Props `<VisitNoteDrafter>` takes MINUS `onSaveDraft`/`initialDraft` — those
 * two are decided HERE, by `DraftAwareVisitNoteDrafter`, never by the caller.
 */
type BaseDrafterProps = Omit<
  VisitNoteDrafterProps,
  "onSaveDraft" | "initialDraft"
>;

/**
 * Adds server-side draft persistence (A7) ON TOP of `<VisitNoteDrafter>`,
 * gated behind `featureFlags.visitNoteDraftPersistence` (default OFF — see
 * that flag's doc comment for the two hard dependencies still outstanding:
 * care-management-gql PR 37723 deployed, and `@astrana/ui` pinned past the
 * release carrying `initialDraft`, astrana-ui PR #599).
 *
 * WHY A SEPARATE COMPONENT, RATHER THAN A CONDITIONAL INSIDE ONE: the
 * persistence hooks (`useOnSaveVisitNoteDraft`, `useVisitNoteDraftInitial`)
 * call `useMutation`/`useQuery`, which THROW when rendered outside an
 * `<ApolloProvider>` — exactly the tree `NotesTab.test.tsx` renders in
 * (only `@astrana/ui/scribe` is mocked there; there is no Apollo context).
 * Branching on the flag INSIDE one component would still call those hooks on
 * every render (rules of hooks: hook calls cannot be skipped by an `if`), so
 * the flag being off would not save a test — or a production host — from
 * needing an Apollo client. Branching on which COMPONENT to render, instead,
 * means the hooks are only ever mounted when the flag is genuinely on.
 */
function DraftAwareVisitNoteDrafter(props: BaseDrafterProps) {
  if (featureFlags.visitNoteDraftPersistence) {
    return <VisitNoteDrafterWithDraftPersistence {...props} />;
  }
  // Flag off: BYTE-FOR-BYTE what this host rendered before A7 existed. No
  // `onSaveDraft`, so `hostOwnsDrafts` is false in the library and every
  // existing local-sessionStorage behavior (autosave + restore) is unchanged.
  return <VisitNoteDrafter {...props} />;
}

function VisitNoteDrafterWithDraftPersistence(props: BaseDrafterProps) {
  const { memberId, encounter } = props;
  const onSaveDraft = useOnSaveVisitNoteDraft({
    memberId,
    ehrPatientId: encounter?.ehrPatientId,
    sourceSystem: encounter?.sourceSystem,
    encounterKey: encounter?.encounterKey,
  });
  // The server-side draft for this chart, if one exists. `initialDraft` landed
  // in @astrana/ui v0.5.24 (PR #599) and this host is pinned past it as of
  // PR #369, so it is passed through now.
  //
  // `undefined` is the normal, safe case — no draft saved yet, the query is
  // still in flight, or it failed. The library treats a late-arriving
  // `initialDraft` as an offer it may decline: it never clobbers content the
  // physician already has on screen (astrana-ui review finding A-1).
  const hostDraft = useVisitNoteDraftInitial(
    encounter?.ehrPatientId,
    encounter?.sourceSystem,
  );

  return (
    <VisitNoteDrafter
      {...props}
      onSaveDraft={onSaveDraft}
      initialDraft={hostDraft.draft}
    />
  );
}

export interface NotesTabProps {
  /** Human member id — same value the rest of the profile uses. */
  memberId?: string;
  /**
   * Astrana's INTERNAL numeric member id for the SAME member `memberId` names.
   * The backend uses it for one thing: loading the note's available lab results
   * (`VisitNoteResultsProvider.forMember(astranaId)`), which is what the
   * drafter's labs picker offers.
   *
   * Both ids must come from ONE resolved member — see
   * `visitNoteMemberIdentity.ts`, which produces the pair and is the only thing
   * that should be feeding these two props. An id from a different member would
   * put someone else's results inside this patient's note.
   *
   * Undefined is a supported, safe state: the library omits the field from the
   * generate input and the picker renders no labs.
   */
  astranaId?: number;
  /** EHR-neutral snapshot of the encounter on screen. */
  encounter?: EhrEncounterSnapshot;
  /**
   * The chart's imaging reports as the HOST read them — already scrubbed and
   * patient-guarded. Converted to the library's `ImagingStudy` shape here, at
   * the one boundary where Elation's vocabulary meets the component library's.
   *
   * Undefined means no read happened (standalone web, injection blocked, no
   * `el8`), and the picker then offers nothing. That is indistinguishable to the
   * physician from a chart with no imaging, which is acceptable ONLY because
   * nothing is claimed either way: the drafter shows the section with no rows
   * rather than asserting "no imaging on file".
   */
  imagingReports?: ElationReportRead[];
  /**
   * The EHR patient id of the chart on screen RIGHT NOW, used as a second lock
   * on the imaging read's patient guard.
   *
   * The primary lock is upstream: the extension calls `buildElationReports` with
   * the id from the tab URL and drops every report that disagrees. This repeats
   * the check at the point of use, against the same value
   * `MemberProfilePage` already trusts to gate WRITES — the highest-stakes gate
   * in this feature. If this id were the wrong one, the write guard would
   * already be broken.
   *
   * Undefined drops nothing (see `imagingStudiesFromReports`): a fail-closed
   * default would blank the picker whenever the id is merely unreadable.
   */
  expectedEhrPatientId?: string;
  /** Host-injected per-section write seam. Absent means no write UI at all. */
  onWriteSection?: WriteSectionFn;
  /**
   * Forwarded verbatim to `VisitNoteDrafter`'s `onDirtyChange` (@astrana/ui,
   * mirroring CreateAuthForm/CreateClaimForm's AM-5694/AM-5921 contract). Lets
   * the host fold "a visit-note draft is in progress" into the same
   * leave/tab-switch veto it already uses for those two forms — see
   * `MemberProfilePage.tsx`'s `noteFormDirtyRef`.
   */
  onDirtyChange?: (isDirty: boolean) => void;
}

export function NotesTab({
  memberId,
  astranaId,
  encounter,
  imagingReports,
  expectedEhrPatientId,
  onWriteSection,
  onDirtyChange,
}: NotesTabProps) {
  const [drafting, setDrafting] = useState(false);

  // The note type the clinician ALREADY has open, resolved here rather than in
  // the library: `@astrana/ui` is EHR-agnostic and this vocabulary is Elation's.
  // Undefined when there is no open note, or when the practice named its
  // template something we don't recognize — the drafter then falls back to its
  // own snapshot-derived guess, which is exactly the pre-wiring behaviour.
  //
  // Read from the `encounter` PROP, which already carries the template name
  // (`toEhrEncounterSnapshot` folds it in as "Office Visit Note · SOAP Note").
  // Subscribing to the bridge again here would be worse than redundant: the
  // hook is not a shared store, so a second subscription starts null and runs
  // its own cold-start refresh — the picker would show the fallback for a
  // second after the tab is opened and then visibly change under the clinician.
  // Keeping this a pure function of props removes that gap entirely.
  const openNoteType = visitNoteTypeFromTemplateName(encounter?.visitType);

  // Elation's report shape → the library's study shape. The two use different
  // KEYS for the same three-state scrub verdict (`status` vs `availability`), so
  // this is a MAP and never a spread — see `imagingStudiesFromReports`, which
  // exists for that reason and is tested against the library's own predicate.
  //
  // Memoised on the reports themselves: the drafter stays mounted for the life of
  // a draft, and a fresh array identity on every poll would hand the picker a new
  // list object several times a second.
  const imagingStudies = useMemo(
    () =>
      imagingReports
        ? imagingStudiesFromReports(imagingReports, expectedEhrPatientId)
        : undefined,
    [imagingReports, expectedEhrPatientId],
  );

  return (
    <div className="space-y-4">
      {/*
        DRAFTER FIRST. Pressing "Create note" used to open the drafter BELOW the
        list of existing notes, so the thing the clinician just asked for
        appeared off-screen behind however many notes the chart happens to have —
        on a chart with five notes the button looked like it did nothing. The
        work in progress belongs at the top; the chart's history is reference
        material underneath it.
      */}
      {drafting && (
        <DraftAwareVisitNoteDrafter
          memberId={memberId}
          // The labs lookup key. Passed LIVE on every render rather than
          // captured at "Create note" time: the drafter latches mounted for the
          // life of a draft (see the file header), so a captured id would keep
          // pointing at the member who was on screen when drafting started even
          // after the chart moved on. Undefined here = no labs, which is the
          // outcome whenever the host cannot say whose they are.
          astranaId={astranaId}
          encounter={encounter}
          // The library models a clinical note and holds NO EHR vocabulary; the
          // host says which Elation section each field targets. Omitting this
          // is not a no-op — the drafter would propose no destination for any
          // field and every write-back row would open unset.
          sectionCandidates={elationSectionCandidates()}
          // THE PRE-FILLED ROS/EXAM WORDING IS OURS, not the library's. Passing
          // this is what makes those sentences changeable without a library
          // release, a pin bump and an extension rebuild. See
          // `visitNoteTemplateDefaults.ts`; today it holds no differences, and
          // passing it anyway is the point — the seam exists and is exercised, so
          // the first real change is one line in that file rather than a
          // three-repo chain.
          clinicalTemplates={VISIT_NOTE_TEMPLATE_OVERRIDES}
          // "Imaging: dropdown menu of imaging results to pull (pull impressions
          // and findings section)" — the reviewing physician's own words. The
          // library offers them; nothing enters the note until she selects it,
          // and every character is the radiologist's.
          imagingStudies={imagingStudies}
          onWriteSection={onWriteSection}
          openNoteType={openNoteType}
          onDirtyChange={onDirtyChange}
        />
      )}

      {/*
        WHAT A WRITE ACTUALLY DOES to the two checklist sections. The review of
        systems and the physical exam do not live in one field in Elation — each
        system has its own textarea, pre-filled with the template's normals — so
        a write fans out across them and APPENDS beneath whatever is already
        there (`planChecklistWrite` / `mergeChecklistFinding` in
        `elationWriteBack.ts`). Nothing in the write-back card can say that: the
        library is EHR-agnostic and shows no message on a successful write, and
        "we kept your finding and added ours below it" is exactly the sort of
        thing a clinician must not have to discover by diffing their own note.
      */}
      {drafting && (
        <p className="text-xs text-muted-foreground">
          Review of systems and physical exam are written into Elation&rsquo;s
          per-system fields, one system at a time. Existing findings are kept —
          the new line is added underneath. Anything with no matching system
          field is added as an entry in the section instead.
        </p>
      )}

      <ChartNotesPanel
        // Withdrawing the handler hides the button, which is what we want once
        // drafting is under way: a second press cannot restart the flow and
        // throw away a draft in progress. The panel's own "start over" is the
        // deliberate path back.
        onCreateNote={drafting ? undefined : () => setDrafting(true)}
      />
    </div>
  );
}
